import { lazy, Suspense, useRef } from 'react';
import { useIntl } from 'react-intl';
import { CircleX } from 'lucide-react';

import ContentManager from 'src/componentes/content-manager';
import { LinearProgress } from 'src/componentes/LinearProgress';
import LoadingContainer from 'src/componentes/loading-container';
import Panel from 'src/componentes/panel';
import useAuth from 'src/hooks/useAuth';
import {
  MODULOS,
  STATUS_PESQUISA_CLIMA,
  TIPO_INTERACAO_SOPHIE,
} from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';
import LoadingScreen from 'src/componentes/LoadingScreen';

import DashboardConteudo from './DashboardConteudo';
import DashboardFiltroChips from './DashboardFiltroChips';
import DashboardFiltroDrawer from './DashboardFiltroDrawer';
import useDashboardFiltros from './useDashboardFiltros';
import DashboardHeaderActions from './DashboardHeaderActions';
import useDashboardAplicacoes from './useDashboardAplicacoes';
import useDashboardLikert from './useDashboardLikert';
import useDashboardNps from './useDashboardNps';
import useDashboardPerguntas from './useDashboardPerguntas';
import useEstatisticasPesquisaClima from './useEstatisticasPesquisaClima';
import useGerarResumoIA from './useGerarResumoIA';

const DashboardMaximizadoDialog = lazy(() =>
  import('./DashboardMaximizadoDialog')
);

const ResumoActioIA = lazy(() => import('src/componentes/ResumoActioIA'));

const blockedStateStyle = {
  alignItems: 'center',
  color: '#98a2b3',
  display: 'flex',
  justifyContent: 'center',
  padding: '48px 16px',
  textAlign: 'center',
};

function DashboardPesquisaClimaTab({
  active,
  aplicacaoRefreshToken,
  entidadeId,
  formularioId,
  recorrente = false,
  statusId,
}) {
  const intl = useIntl();
  const { modules, terms: resources } = useAuth();
  const hasLoadedRef = useRef(false);

  const isEntidadePersisted = !!entidadeId;

  if (active) {
    hasLoadedRef.current = true;
  }

  const shouldLoad = isEntidadePersisted && (active || hasLoadedRef.current);
  const usos = useDashboardAplicacoes(entidadeId, {
    enabled: shouldLoad && recorrente,
    refreshToken: aplicacaoRefreshToken,
  });
  const filtros = useDashboardFiltros(undefined, {
    aplicacoes: usos.aplicacoes,
    recorrente,
  });
  const shouldLoadDados =
    shouldLoad &&
    (!recorrente ||
      usos.aplicacoes.some(
        (uso) => uso.formularioUsoId === filtros.filtroAplicado.formularioUsoId
      ));

  const nps = useDashboardNps(entidadeId, {
    enabled: shouldLoadDados,
    active,
    filtros: filtros.parametros,
  });
  const likert = useDashboardLikert(entidadeId, {
    enabled: shouldLoadDados,
    filtros: filtros.parametros,
    refreshToken: nps.refreshToken,
  });
  const estatisticas = useEstatisticasPesquisaClima(entidadeId, {
    enabled: shouldLoadDados,
    filtros: filtros.parametros,
    refreshToken: nps.refreshToken,
  });
  const perguntas = useDashboardPerguntas(entidadeId, {
    enabled: shouldLoadDados,
    formularioId,
    filtros: filtros.parametros,
    refreshToken: nps.refreshToken,
  });

  const gerarResumoIA = useGerarResumoIA(entidadeId);

  const resumoIA =
    gerarResumoIA.resumoGerado ?? estatisticas.dados?.resumoPesquisaIA ?? null;
  const requestIdResumoIA =
    gerarResumoIA.requestIdGerado ??
    estatisticas.dados?.requestIdResumoPesquisaIA ??
    null;

  const hasActioIA = modules?.some(
    (module) => module.id === MODULOS.INTELIGENCIA_ARTIFICIAL && module.visivel
  );
  const podeGerarInsights = hasActioIA && !resumoIA;
  const insightsStatusOk = Number(statusId) === STATUS_PESQUISA_CLIMA.ENCERRADA;

  const handleGerarInsights = () => {
    gerarResumoIA.gerarResumo();
  };

  const handleMaximizar = () => {
    ContentManager.addContent(
      <DashboardMaximizadoDialog
        entidadeId={entidadeId}
        aplicacoes={usos.aplicacoes}
        autoRefreshInicial={nps.autoRefreshMinutos}
        formularioId={formularioId}
        filtroInicial={filtros.filtroAplicado}
        recorrente={recorrente}
      />
    );
  };

  const renderBlockedMessage = () => (
    <div style={blockedStateStyle}>
      {intl.formatMessage(
        { id: 'labelSalvePesquisaClimaAntesVisualizarRespostas' },
        { pesquisa: resources.pesquisa }
      )}
    </div>
  );

  return (
    <LoadingContainer
      isLoading={
        usos.isLoading ||
        nps.isLoading ||
        likert.isLoading ||
        perguntas.isLoading
      }
    >
      <Panel
        header={capitalizeFirstLetter(
          intl.formatMessage({ id: 'labelDashboard' })
        )}
        headerAside={
          isEntidadePersisted ? (
            <DashboardHeaderActions
              onOpenFiltro={filtros.onOpen}
              temFiltros={filtros.temFiltros}
              autoRefreshMinutos={nps.autoRefreshMinutos}
              isLoading={
                nps.isLoading ||
                likert.isLoading ||
                perguntas.isLoading ||
                estatisticas.isLoading ||
                gerarResumoIA.isGenerating
              }
              onChangeAutoRefresh={nps.setAutoRefreshMinutos}
              onGerarInsights={
                podeGerarInsights && insightsStatusOk
                  ? handleGerarInsights
                  : undefined
              }
              gerarInsightsTooltip={
                podeGerarInsights
                  ? intl.formatMessage({
                      id: insightsStatusOk
                        ? 'gerarResumoButtonEnabled'
                        : 'gerarResumoButtonDisabled',
                    })
                  : undefined
              }
              onMaximizar={handleMaximizar}
              onRefresh={nps.refresh}
            />
          ) : null
        }
      >
        {!isEntidadePersisted && renderBlockedMessage()}
        {isEntidadePersisted && (active || hasLoadedRef.current) && (
          <>
            <DashboardFiltroChips
              aplicacoes={usos.aplicacoes}
              filtros={filtros.filtroAplicado}
              onRemove={filtros.onRemoveFiltro}
            />
            {(gerarResumoIA.isGenerating || gerarResumoIA.erro) &&
              !resumoIA && (
                <div
                  style={{
                    background: '#FAFAFA',
                    border: '1px solid #E4E7EC',
                    borderRadius: '0.5rem',
                    marginBottom: '1rem',
                    padding: '1rem',
                  }}
                >
                  {gerarResumoIA.isGenerating && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '0.25rem 0',
                      }}
                    >
                      <LinearProgress style={{ width: '50%' }} />
                    </div>
                  )}
                  {gerarResumoIA.erro && (
                    <>
                      <div
                        style={{
                          alignItems: 'center',
                          color: '#1D2939',
                          display: 'flex',
                          fontWeight: 600,
                          fontSize: '0.9375rem',
                          gap: '0.375rem',
                        }}
                      >
                        <CircleX color="#D92D20" size={16} />
                        {intl.formatMessage({
                          id: 'labelErroGerarInsightsTitulo',
                        })}
                      </div>
                      <div
                        style={{
                          color: '#667085',
                          fontSize: '0.875rem',
                          marginTop: '0.25rem',
                        }}
                      >
                        {intl.formatMessage({
                          id: 'labelErroGerarInsightsDescricao',
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            {resumoIA && (
              <Suspense fallback={<LoadingScreen />}>
                <ResumoActioIA
                  isNew={!!gerarResumoIA.resumoGerado}
                  requestId={requestIdResumoIA}
                  resumo={resumoIA || undefined}
                  interactionType={
                    TIPO_INTERACAO_SOPHIE.INSIGHTS_PESQUISA_CLIMA
                  }
                />
              </Suspense>
            )}
            {recorrente && !usos.isLoading && !usos.aplicacoes.length ? (
              <div style={blockedStateStyle}>
                {intl.formatMessage({
                  id: 'msgNenhumaAplicacaoPesquisaClima',
                })}
              </div>
            ) : (
              <DashboardConteudo
                aplicacoes={usos.aplicacoes}
                dados={nps.dados}
                dadosLikert={likert.dados}
                dadosPerguntas={perguntas.dados}
                entidadeId={entidadeId}
                estatisticas={estatisticas.dados}
                estatisticasIsLoading={estatisticas.isLoading}
                filtros={filtros.parametros}
                recorrente={recorrente}
                refreshToken={nps.refreshToken}
              />
            )}
          </>
        )}
        <DashboardFiltroDrawer {...filtros.drawerProps} />
      </Panel>
    </LoadingContainer>
  );
}

export default DashboardPesquisaClimaTab;
